import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";

const localDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isMedicationActive = (medication, now) => {
  const durationDays = Number(medication.duration_days || 0);
  if (!durationDays) return true;

  const createdAt = medication.created_at || medication.createdAt;
  if (!createdAt) return true;

  const createdTime = new Date(createdAt).getTime();
  return !Number.isFinite(createdTime) || now < createdTime + durationDays * 24 * 60 * 60 * 1000;
};

/**
 * Foreground browser-notification hook for meal logging and medication reminders.
 * Checks every 60s while the protected app layout is open.
 */
export function useReminders() {
  const { user } = useAuth();
  const { lang } = useLang();
  const firedRef = useRef(new Set());
  const [settingsVersion, setSettingsVersion] = useState(0);
  const guestMode =
    typeof window !== "undefined" &&
    localStorage.getItem("guestMode") === "true";
  const reminderOwnerId = guestMode ? "guest" : user?.id;

  useEffect(() => {
    const reloadSettings = () => setSettingsVersion((current) => current + 1);
    window.addEventListener("ddp-reminders-updated", reloadSettings);
    return () => window.removeEventListener("ddp-reminders-updated", reloadSettings);
  }, []);

  useEffect(() => {
    if (!reminderOwnerId) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;

    const mealRemindersKey = `ddp-meal-reminders-${reminderOwnerId}`;
    const medicationsKey = `ddp-medications-${reminderOwnerId}`;
    const medicationNotificationsKey = `ddp-medication-notifications-${reminderOwnerId}`;
    const medicationFiredKey = `ddp-medication-notifications-fired-${reminderOwnerId}`;

    let reminders = null;
    let medicationNotifications = null;
    try {
      const savedMealReminders = localStorage.getItem(mealRemindersKey);
      const savedMedicationNotifications = localStorage.getItem(medicationNotificationsKey);
      reminders = savedMealReminders ? JSON.parse(savedMealReminders) : null;
      medicationNotifications = savedMedicationNotifications ? JSON.parse(savedMedicationNotifications) : null;
    } catch {
      return;
    }

    if (!reminders?.enabled && !medicationNotifications?.enabled) return;

    let firedMedicationDate = localDateKey();
    let firedMedicationKeys = new Set();
    try {
      const savedFired = JSON.parse(localStorage.getItem(medicationFiredKey) || "[]");
      if (Array.isArray(savedFired)) {
        firedMedicationKeys = new Set(savedFired.filter((key) => key.startsWith(`${firedMedicationDate}-`)));
      }
    } catch {
      localStorage.removeItem(medicationFiredKey);
    }

    const sendMealReminder = (reminderType, dateKey) => {
      const key = `${dateKey}-${reminderType}`;
      if (firedRef.current.has(key)) return;
      if (Notification.permission !== "granted") return;
      const titles = {
        en: { breakfast: "Time for breakfast 🥗", lunch: "Time for lunch 🍽️", dinner: "Time for dinner 🌙" },
        ar: { breakfast: "حان وقت الفطور", lunch: "حان وقت الغداء", dinner: "حان وقت العشاء" },
      };
      const bodies = {
        en: { breakfast: "Don't forget to log your breakfast in Dialysis Diet Pro.", lunch: "Log lunch so your nutrients stay on track.", dinner: "Log dinner and check today's summary." },
        ar: { breakfast: "لا تنسَ تسجيل وجبتك في دايلاسيس دايت برو.", lunch: "سجل الغداء حتى تبقى مغذياتك على المسار الصحيح.", dinner: "سجل العشاء وراجع ملخص اليوم." },
      };
      try {
        new Notification(titles[lang][reminderType] || titles.en[reminderType], {
          body: bodies[lang][reminderType] || bodies.en[reminderType],
          tag: key,
        });
        firedRef.current.add(key);
      } catch {}
    };

    const sendMedicationReminder = (medication, time, dateKey) => {
      const key = `${dateKey}-${medication.id}-${time}`;
      if (firedMedicationKeys.has(key)) return;
      if (Notification.permission !== "granted") return;

      try {
        new Notification("Medication Reminder", {
          body: `Time to take ${medication.name}. Always follow your doctor’s instructions.`,
          tag: key,
        });
        firedMedicationKeys.add(key);
        localStorage.setItem(medicationFiredKey, JSON.stringify([...firedMedicationKeys]));
      } catch {}
    };

    const tick = () => {
      const now = new Date();
      const dateKey = localDateKey(now);
      const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

      if (reminders?.enabled) {
        const mealDateKey = now.toISOString().slice(0, 10);
        if (hhmm === reminders.breakfast) sendMealReminder("breakfast", mealDateKey);
        if (hhmm === reminders.lunch) sendMealReminder("lunch", mealDateKey);
        if (hhmm === reminders.dinner) sendMealReminder("dinner", mealDateKey);
      }

      if (!medicationNotifications?.enabled) return;

      if (dateKey !== firedMedicationDate) {
        firedMedicationDate = dateKey;
        firedMedicationKeys = new Set();
      }

      try {
        const medications = JSON.parse(localStorage.getItem(medicationsKey) || "[]");
        if (!Array.isArray(medications)) return;

        medications
          .filter((medication) => (
            medication &&
            medication.id &&
            typeof medication.name === "string" &&
            isMedicationActive(medication, now.getTime())
          ))
          .forEach((medication) => {
            if (Array.isArray(medication.times) && medication.times.includes(hhmm)) {
              sendMedicationReminder(medication, hhmm, dateKey);
            }
          });
      } catch {}
    };

    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, [reminderOwnerId, lang, settingsVersion]);
}
